"use client";

import { Icons } from "@/app/assets/assets";
import PrimaryBtn from "@/components/PrimaryBtn";
import Form from "@/HomeFeatures/Contact/Form";
import { useTheme } from "@/HomeFeatures/Context/ThemeContext";
import Image from "next/image";
import React from "react";

interface Logo {
  src: string;
  name: string;
}

interface BannerProps {
  title: React.ReactNode;
  desc: React.ReactNode;
  buttonTitle: React.ReactNode;
  logos?: Logo[];
}

const AiBanner = ({ title, desc, buttonTitle, logos }: BannerProps) => {
  const { handleOpenQuote } = useTheme();

  return (
    <div className="w-[calc(100%_-_10%)] mx-auto max-w-[2500px] py-[60px] pb-[0px]">
      {/* Hero row */}
      <div className="flex flex-col lg:flex-row items-center gap-[30px] lg:gap-[40px] justify-between">
        {/* Content */}
        <div className="flex flex-col lg:gap-[15px] gap-[5px] w-full lg:w-auto">
          <h1
            className="
              max-w-[18ch]
              justify-start
              text-white
              text-[length:var(--service-banner-title)]
              flex
              lg:gap-x-[20px]
              gap-x-[10px]
              items-center
              font-normal
              clash-font
              capitalize
              flex-wrap
              leading-[100%]
            "
          >
            {title}
          </h1>

          <p
            className="
              justify-start
              text-white/50
              text-[length:var(--banner-para)]
              max-w-full
              sm:max-w-[90%]
              lg:max-w-[47ch]
              font-normal
              leading-[150%]
              tracking-tight
              py-3
              lg:py-4
            "
          >
            {desc}
          </p>

          <PrimaryBtn
            text={buttonTitle}
            iconSrc={Icons?.chevronBlue}
            onClick={() => handleOpenQuote()}
          />
        </div>

        {/* Form */}
        <div className="flex w-full max-w-[670px] flex-col items-center">
          <Form variant="dark" type="ai_forms" />
        </div>
      </div>

      {/* Trusted By — optional */}
      {logos && logos.length > 0 && (
        <div className="mt-10 sm:mt-12 lg:mt-[80px]">
          {/* Trusted By heading */}
          <div className="flex items-center justify-center gap-3 sm:gap-4 mb-8 sm:mb-10 lg:mb-12">
            <span className="h-px w-10 sm:w-14 lg:w-20 bg-[linear-gradient(90deg,#626262_0%,#FFF_100%)]" />

            <p className="text-white/70 text-[14px] sm:text-[18px] lg:text-[26px] font-medium uppercase tracking-[0.15em] sm:tracking-[0.2em] lg:tracking-widest whitespace-nowrap">
              Trusted By
            </p>

            <span className="h-px w-10 sm:w-14 lg:w-20 bg-[linear-gradient(90deg,#626262_0%,#FFF_100%)] rotate-180" />
          </div>

          {/* Logos marquee */}
          <div
            className="relative overflow-hidden w-full"
            style={{
              maskImage:
                "linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)",
              WebkitMaskImage:
                "linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)",
            }}
          >
            <div className="flex w-max animate-marquee gap-8 sm:gap-12 lg:gap-[70px] items-center">
              {[...logos, ...logos].map((logo, i) => (
                <div
                  key={i}
                  className="
                    flex-shrink-0
                    flex items-center justify-center
                    h-[80px] sm:h-[100px] lg:h-[130px]
                    opacity-60
                    hover:opacity-100
                    transition-opacity duration-300
                  "
                >
                  <Image
                    src={logo.src}
                    alt={logo.name}
                    width={160}
                    height={120}
                    className="
                      h-full
                      w-auto
                      max-w-[100px]
                      sm:max-w-[120px]
                      lg:max-w-[140px]
                      object-contain
                      grayscale
                      hover:grayscale-0
                    "
                    sizes="(max-width: 640px) 100px, (max-width: 1024px) 120px, 140px"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AiBanner;
